import i18n from './index';

/**
 * Display labels for enum codes that arrive from the API (`PENDING_REVIEW`, `clinic_admin`, `PAID`).
 *
 * The code is an identifier: it is what the backend stores, filters and audits on, so it is never
 * translated or changed. Only its LABEL is looked up, in the `enums` namespace, keyed by a group
 * name and the code exactly as the API sends it:
 *
 *   enumLabel('referralStatus', 'UNDER_REVIEW')   en → "Under review"   hi → "समीक्षा में"
 *
 * A code with no catalogue entry degrades to a humanised English label ("Under review") — never to
 * a raw key, never to blank. That keeps a newly added backend value readable on the day it ships,
 * and a test lists codes that are missing so they get translated.
 *
 * Clinical identifiers are NOT enums in this sense and must not go through here: drug, test, unit
 * and frequency codes render verbatim (clinicalSafety.ts, rule 1).
 */
export function humanizeCode(code: string): string {
  const words = String(code).replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

export function enumLabel(group: string, code: string | null | undefined): string {
  if (code === null || code === undefined || code === '') return '';
  const raw = String(code);
  // The same code reaches the UI in different cases from different endpoints (`clinic_admin` from
  // the JWT, `CLINIC_ADMIN` from some DTOs). i18next tries each key in turn, so one catalogue entry
  // serves both, whichever case it was written in.
  const keys = [...new Set([raw, raw.toUpperCase(), raw.toLowerCase()])].map((c) => `enums:${group}.${c}`);
  return i18n.t(keys, { defaultValue: humanizeCode(raw) });
}
